// homeSchema.js — structured data for https://www.prism-me.com/
//
// FAQ text below must stay word-for-word identical to the FAQs shown on the page.
// If you edit an FAQ on the page, edit it here too (English and Arabic).

const SITE = "https://www.prism-me.com";

function faq(question, answer) {
  return {
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  };
}

function offer(name, path) {
  return {
    "@type": "Offer",
    itemOffered: {
      "@type": "Service",
      name,
      url: `${SITE}${path}`,
    },
  };
}

export const homeSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: "Prism Digital",
      alternateName: ["Prism Digital Marketing", "Prism"],
      legalName: "Prism Digital Marketing Management LLC",
      url: `${SITE}/`,
      logo: {
        "@type": "ImageObject",
        "@id": `${SITE}/#logo`,
        url: `${SITE}/logo@2x.png`,
        contentUrl: `${SITE}/logo@2x.png`,
        caption: "Prism Digital logo",
      },
      image: { "@id": `${SITE}/#logo` },
      description:
        "Prism Digital is an award-winning digital marketing agency in Dubai offering SEO, PPC, social media, branding, web design and advertising across the UAE and GCC.",
      foundingDate: "20 Years",
      founder: { "@id": `${SITE}/#lovetto-nazareth` },
      email: "sales@prism-me.com",
      telephone: "+971-4-332-0808",
      address: {
        "@type": "PostalAddress",
        streetAddress:
          "Office No. 604 - West Wing, Latifa Tower, World Trade Center, 1 Sheikh Zayed Road",
        addressLocality: "Dubai",
        addressRegion: "Dubai",
        addressCountry: "AE",
      },
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "sales",
          telephone: "+971-4-332-0808",
          email: "sales@prism-me.com",
          areaServed: "AE",
          availableLanguage: ["English", "Arabic"],
        },
        {
          "@type": "ContactPoint",
          contactType: "customer service",
          email: "info@prism-me.com",
          url: `${SITE}/contact`,
          availableLanguage: ["English", "Arabic"],
        },
      ],
      sameAs: [
        "https://www.facebook.com/PrismSocial/",
        "https://www.instagram.com/prismsocial/",
        "https://www.linkedin.com/company/prismmarketing",
        "https://x.com/PRISM_Social",
        "https://www.youtube.com/@PrismDigitalMarketingAgency",
        "https://www.tiktok.com/@prismsocial",
        "https://www.pinterest.com/prismsocial/",
      ],
      knowsAbout: [
        "Search Engine Optimization",
        "Generative Engine Optimization",
        "Answer Engine Optimization",
        "Pay-Per-Click Advertising",
        "Social Media Marketing",
        "Web Design",
        "Web Development",
        "Branding",
        "Video Production",
        "Mobile App Development",
        "Public Relations",
        "E-commerce Marketing",
      ],
      employee: [{ "@id": `${SITE}/author/binisha#person` }],
    },
    {
      "@type": "ProfessionalService",
      "@id": `${SITE}/#localbusiness`,
      name: "Prism Digital",
      parentOrganization: { "@id": `${SITE}/#organization` },
      url: `${SITE}/`,
      image: `${SITE}/banners/homebanner.webp`,
      logo: { "@id": `${SITE}/#logo` },
      telephone: "+971-4-332-0808",
      email: "sales@prism-me.com",
      priceRange: "$$",
      address: {
        "@type": "PostalAddress",
        streetAddress:
          "Office No. 604 - West Wing, Latifa Tower, World Trade Center, 1 Sheikh Zayed Road",
        addressLocality: "Dubai",
        addressRegion: "Dubai",
        addressCountry: "AE",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 25.2216463,
        longitude: 55.2811041,
      },
      hasMap: "https://maps.google.com/?cid=15301595664249223322",
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "21:00",
        },
      ],
      areaServed: [
        { "@type": "City", name: "Dubai" },
        { "@type": "City", name: "Abu Dhabi" },
        { "@type": "City", name: "Sharjah" },
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Place", name: "GCC" },
      ],
      hasOfferCatalog: { "@id": `${SITE}/#services` },
    },
    {
      "@type": "Person",
      "@id": `${SITE}/#lovetto-nazareth`,
      name: "Lovetto Nazareth",
      jobTitle: "Founder & CEO",
      worksFor: { "@id": `${SITE}/#organization` },
      url: `${SITE}/about-prism-digital-marketing-agency`,
    },
    {
      "@type": "Person",
      "@id": `${SITE}/author/binisha#person`,
      name: "Binisha C",
      jobTitle: "SEO Manager",
      url: `${SITE}/author/binisha`,
      worksFor: { "@id": `${SITE}/#organization` },
      knowsAbout: [
        "Technical SEO",
        "Local SEO",
        "Google Business Profile",
        "AEO",
        "GEO",
      ],
    },
    {
      "@type": "OfferCatalog",
      "@id": `${SITE}/#services`,
      name: "Digital Marketing Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: { "@id": `${SITE}/seo-company-dubai#service` },
        },
        {
          "@type": "Offer",
          itemOffered: { "@id": `${SITE}/seo-company-abu-dhabi#service` },
        },
        offer(
          "PPC Campaign Management",
          "/paid-media-agency/ppc-campaign-management-in-dubai",
        ),
        offer(
          "Social Media Marketing Agency Dubai",
          "/social-media-marketing-agency-in-dubai",
        ),
        offer("Web Design Agency Dubai", "/web-design-agency"),
        offer(
          "Website Development Company Dubai",
          "/website-development-company-in-dubai",
        ),
        offer("Branding Agency Dubai", "/branding-agency-dubai"),
        offer(
          "Video Production Company Dubai",
          "/video-production-company-dubai",
        ),
        offer(
          "Mobile App Development Company Dubai",
          "/mobile-app-development-company-in-dubai",
        ),
        offer("PR Agency Dubai", "/pr-agency-dubai"),
        offer("E-commerce Marketing Agency", "/e-commerce-marketing-agency"),
        offer("Event Management", "/events-management"),
      ],
    },
    {
      "@type": "Service",
      "@id": `${SITE}/seo-company-dubai#service`,
      name: "SEO Services in Dubai",
      serviceType: "Search engine optimization",
      url: `${SITE}/seo-company-dubai`,
      description:
        "Technical, local, eCommerce, Arabic and AI search SEO (GEO and AEO) for businesses in Dubai and the UAE.",
      provider: { "@id": `${SITE}/#organization` },
      areaServed: [
        { "@type": "City", name: "Dubai" },
        { "@type": "Country", name: "United Arab Emirates" },
      ],
      offers: {
        "@type": "Offer",
        priceSpecification: {
          "@type": "PriceSpecification",
          minPrice: 3000,
          priceCurrency: "AED",
          unitText: "MONTH",
        },
      },
    },
    {
      "@type": "Service",
      "@id": `${SITE}/seo-company-abu-dhabi#service`,
      name: "SEO Services in Abu Dhabi",
      serviceType: "Search engine optimization",
      url: `${SITE}/seo-company-abu-dhabi`,
      description:
        "Technical, local, Arabic and AI search SEO for businesses in Abu Dhabi, Al Ain and the wider UAE.",
      provider: { "@id": `${SITE}/#organization` },
      areaServed: [
        { "@type": "City", name: "Abu Dhabi" },
        { "@type": "City", name: "Al Ain" },
        { "@type": "Country", name: "United Arab Emirates" },
      ],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE}/#website`,
      url: `${SITE}/`,
      name: "Prism Digital",
      alternateName: "Prism Digital Marketing Agency",
      publisher: { "@id": `${SITE}/#organization` },
      inLanguage: ["en", "ar"],
    },
    {
      "@type": "WebPage",
      "@id": `${SITE}/#webpage`,
      url: `${SITE}/`,
      name: "Best Digital Marketing Agency in Dubai, UAE | Prism Digital",
      description:
        "Prism Digital is an award-winning digital marketing agency in Dubai offering SEO, PPC, social media, branding, web design & advertising. Get a free audit today.",
      isPartOf: { "@id": `${SITE}/#website` },
      about: { "@id": `${SITE}/#organization` },
      primaryImageOfPage: {
        "@type": "ImageObject",
        url: `${SITE}/banners/homebanner.webp`,
      },
      breadcrumb: { "@id": `${SITE}/#breadcrumb` },
      inLanguage: "en",
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${SITE}/#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      ],
    },
    {
      "@type": "FAQPage",
      "@id": `${SITE}/#faq`,
      isPartOf: { "@id": `${SITE}/#webpage` },
      mainEntity: [
        faq(
          "How do I determine my marketing budget?",
          "Depending on your objectives, marketing usually accounts for 5–10% of your total income, but where you spend your money matters just as much as how much you spend. For the best return on investment, Prism Digital assists you in making wise investments.",
        ),
        faq(
          "How can Prism Digital's Paid Media services benefit my business?",
          "Services on Paid Media from Prism Digital Marketing Management LLC ensure the delivery of targeted advertisement campaigns that manage traffic flow, convey potential customers, and boost sales. Using popular platforms such as Google Ads, Facebook, and Instagram, we get your ads to the essential times and audiences at their proper spots to maximize the budget against all measurable results",
        ),
        faq(
          "What makes Prism Digital's web design and development services unique?",
          "Prism Digital prioritizes user experience, function, and visual appeal in its digital marketing services. Depending on the brand's needs, it custom-creates responsive and SEO-friendly web application development that engages users, ranks well on search engines, and converts visitors to clients.",
        ),
        faq(
          "What is the cost of hiring a marketing agency in the UAE?",
          "It depends from agency to agency, as diverse costs are incurred in the UAE based on the required services, plus it's affected by the extent of your campaign. Prism Digital SEO agency rates are flexible to your business requirements to ensure that money is well-spent on each.",
        ),
        faq(
          "What is the best digital marketing agency in Dubai?",
          "Prism Digital is consistently ranked among the top 10 digital marketing agencies in Dubai, with over 20 years of experience and 1,300+ verified client reviews across Google, Clutch and GoodFirms. The right agency for you depends on your specific goals, budget and industry. We offer a free audit to help you decide.",
        ),
        faq(
          "How much does digital marketing cost in the UAE?",
          "Digital marketing budgets in the UAE typically range from a few thousand to several hundred thousand dirhams per month, depending on channels, ad spend and company size. Most experts recommend allocating 5–10% of revenue to marketing; Prism tailors packages to your specific budget and objectives.",
        ),
        faq(
          "Which agency is best for social media marketing in Dubai?",
          "Look for a social media agency with a proven content and paid-social track record in your industry, transparent reporting, and local UAE market experience. Prism Digital manages organic content, community management and paid social campaigns for brands across retail, real estate and hospitality in the UAE.",
        ),
        faq(
          "What does an SEO agency in Dubai actually do?",
          "An SEO agency improves how easily your website is found on Google for the searches your customers actually use — through technical fixes, content strategy, and authoritative link building. Prism's SEO team also optimizes for structured data so your content is eligible for featured snippets and AI-generated answers.",
        ),
        faq(
          "How do I choose a PPC agency in Dubai?",
          "Choose a PPC agency that reports against your actual business metrics (cost per lead, ROAS) rather than clicks or impressions alone, and that has verifiable case studies in your sector. Prism runs Google, Meta and TikTok campaigns with weekly performance reporting tied to agreed KPIs.",
        ),
        faq(
          "Does Prism Digital work outside Dubai?",
          "Yes. While Prism is headquartered in Dubai, we deliver digital marketing, advertising and branding services across Abu Dhabi, Sharjah and the wider UAE and GCC region.",
        ),
      ],
    },
  ],
};

// ───────────────────────── Arabic (/ar) ─────────────────────────
// Same graph and same @ids (no second Organization). Only the page-level
// nodes change: WebPage, BreadcrumbList and FAQPage.

const AR_NAME = "بريزم ديجيتال | أفضل شركة للتسويق الإلكتروني في الإمارات";

const AR_DESCRIPTION =
  "بريزم ديجيتال هي وكالة تسويق رقمي حائزة على جوائز في دبي، تقدم خدمات تحسين محركات البحث والإعلانات المدفوعة وإدارة وسائل التواصل الاجتماعي والعلامات التجارية وتصميم المواقع والإعلان. احصل على تدقيق مجاني اليوم.";

const AR_FAQS = [
  faq(
    "كيف أحدد ميزانيتي التسويقية؟",
    "تمثل ميزانية التسويق عادةً ما بين 5% و10% من إجمالي دخلك بحسب أهدافك، لكن مكان إنفاق أموالك لا يقل أهمية عن حجم ما تنفقه. وللحصول على أفضل عائد على الاستثمار، تساعدك بريزم ديجيتال على اتخاذ قرارات استثمارية سليمة.",
  ),
  faq(
    "كيف يمكن لخدمات الإعلانات المدفوعة من بريزم ديجيتال أن تفيد عملي؟",
    "تضمن خدمات الإعلانات المدفوعة من شركة بريزم ديجيتال للتسويق الرقمي ذ.م.م تنفيذ حملات إعلانية موجهة تتحكم في تدفق الزيارات وتجذب العملاء المحتملين وتزيد المبيعات. ومن خلال منصات شهيرة مثل إعلانات جوجل وفيسبوك وإنستغرام، نوصل إعلاناتك إلى الجمهور المناسب في الوقت والمكان المناسبين لتحقيق أقصى استفادة من الميزانية بنتائج قابلة للقياس.",
  ),
  faq(
    "ما الذي يميز خدمات تصميم وتطوير المواقع من بريزم ديجيتال؟",
    "تعطي بريزم ديجيتال الأولوية لتجربة المستخدم والأداء الوظيفي والجاذبية البصرية في خدماتها للتسويق الرقمي. وبحسب احتياجات العلامة التجارية، نصمم تطبيقات ويب متجاوبة ومتوافقة مع محركات البحث تجذب المستخدمين وتتصدر نتائج البحث وتحوّل الزوار إلى عملاء.",
  ),
  faq(
    "كم تبلغ تكلفة التعاقد مع شركة تسويق في الإمارات؟",
    "تختلف التكلفة من شركة لأخرى بحسب الخدمات المطلوبة ونطاق حملتك. وأسعار بريزم ديجيتال لخدمات تحسين محركات البحث مرنة وتتناسب مع احتياجات عملك لضمان إنفاق كل درهم في مكانه الصحيح.",
  ),
  faq(
    "ما هي أفضل شركة تسويق رقمي في دبي؟",
    "تُصنَّف بريزم ديجيتال باستمرار ضمن أفضل 10 شركات تسويق رقمي في دبي، بخبرة تزيد على 20 عاماً وأكثر من 1,300 تقييم موثق من العملاء على جوجل وClutch وGoodFirms. وتعتمد الشركة الأنسب لك على أهدافك وميزانيتك ومجال عملك. نقدم تدقيقاً مجانياً لمساعدتك على اتخاذ القرار.",
  ),
  faq(
    "كم تبلغ تكلفة التسويق الرقمي في الإمارات؟",
    "تتراوح ميزانيات التسويق الرقمي في الإمارات عادةً من بضعة آلاف إلى عدة مئات الآلاف من الدراهم شهرياً، بحسب القنوات والإنفاق الإعلاني وحجم الشركة. ويوصي معظم الخبراء بتخصيص 5% إلى 10% من الإيرادات للتسويق، وتصمم بريزم باقات مناسبة لميزانيتك وأهدافك.",
  ),
  faq(
    "ما أفضل شركة للتسويق عبر وسائل التواصل الاجتماعي في دبي؟",
    "ابحث عن شركة تسويق عبر وسائل التواصل الاجتماعي لديها سجل مثبت في المحتوى والإعلانات المدفوعة في مجالك، وتقارير شفافة، وخبرة في السوق الإماراتي. تدير بريزم ديجيتال المحتوى العضوي وإدارة المجتمعات وحملات التواصل الاجتماعي المدفوعة لعلامات تجارية في قطاعات التجزئة والعقارات والضيافة في الإمارات.",
  ),
  faq(
    "ماذا تفعل شركة تحسين محركات البحث في دبي بالضبط؟",
    "تعمل شركة تحسين محركات البحث على تسهيل العثور على موقعك في جوجل عند البحث بالعبارات التي يستخدمها عملاؤك فعلياً، من خلال الإصلاحات التقنية واستراتيجية المحتوى وبناء الروابط الموثوقة. كما يعمل فريق بريزم على تحسين البيانات المنظمة لتكون أهلاً للظهور في المقتطفات المميزة والإجابات المولدة بالذكاء الاصطناعي.",
  ),
  faq(
    "كيف أختار شركة إعلانات مدفوعة (PPC) في دبي؟",
    "اختر شركة تقدم تقاريرها وفق مؤشرات أعمالك الفعلية (تكلفة العميل المحتمل، العائد على الإنفاق الإعلاني) وليس النقرات أو مرات الظهور فقط، ولديها دراسات حالة موثقة في قطاعك. تدير بريزم حملات جوجل وميتا وتيك توك مع تقارير أداء أسبوعية مرتبطة بمؤشرات أداء متفق عليها.",
  ),
  faq(
    "هل تعمل بريزم ديجيتال خارج دبي؟",
    "نعم. ورغم أن مقر بريزم الرئيسي في دبي، فإننا نقدم خدمات التسويق الرقمي والإعلان وبناء العلامات التجارية في أبوظبي والشارقة وبقية الإمارات ومنطقة دول مجلس التعاون الخليجي.",
  ),
];

export function getHomeSchema(isArabic = false) {
  if (!isArabic) return homeSchema;

  return {
    ...homeSchema,
    "@graph": homeSchema["@graph"].map((node) => {
      switch (node["@type"]) {
        case "WebPage":
          return {
            ...node,
            "@id": `${SITE}/ar#webpage`,
            url: `${SITE}/ar`,
            name: AR_NAME,
            description: AR_DESCRIPTION,
            breadcrumb: { "@id": `${SITE}/ar#breadcrumb` },
            inLanguage: "ar",
          };
        case "BreadcrumbList":
          return {
            ...node,
            "@id": `${SITE}/ar#breadcrumb`,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "الرئيسية",
                item: `${SITE}/ar`,
              },
            ],
          };
        case "FAQPage":
          return {
            ...node,
            "@id": `${SITE}/ar#faq`,
            isPartOf: { "@id": `${SITE}/ar#webpage` },
            mainEntity: AR_FAQS,
          };
        default:
          return node;
      }
    }),
  };
}

export default homeSchema;
